/**
 * Admin blog analytics — per-post views, unique visitors, avg / total
 * time-on-page, engaged views (>=15s). Data comes from
 * `GET /api/admin/blog/analytics?days=30`. The tracker is public — see
 * `pages/BlogPostPage.jsx#useBlogViewTracker`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, ChevronLeft, Eye, Users, Clock, Zap, TrendingUp, BookOpen, ExternalLink, Share2, Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { adminBlogAnalytics } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtSec = (s) => {
  const n = Number(s || 0);
  if (n < 60) return `${Math.round(n)} mp`;
  const m = Math.floor(n / 60);
  const sec = Math.round(n - m * 60);
  return sec ? `${m} p ${sec} mp` : `${m} p`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('hu-HU', { year: '2-digit', month: '2-digit', day: '2-digit' }) : '—');

const WINDOWS = [
  { d: 7,  label: '7 nap' },
  { d: 30, label: '30 nap' },
  { d: 90, label: '90 nap' },
  { d: 0,  label: 'Összes' },
];

// Forgalmi források — a referrer alapján osztályozva a backendben.
const SOURCE_META = [
  { k: 'google',   label: 'Google',           bar: 'bg-yellow-500',  text: 'text-yellow-400' },
  { k: 'search',   label: 'Más kereső',       bar: 'bg-amber-700',   text: 'text-amber-500' },
  { k: 'internal', label: 'Belső link',       bar: 'bg-blue-500',    text: 'text-blue-400' },
  { k: 'social',   label: 'Közösségi média',  bar: 'bg-pink-500',    text: 'text-pink-400' },
  { k: 'direct',   label: 'Közvetlen / ismeretlen', bar: 'bg-gray-600', text: 'text-gray-400' },
  { k: 'other',    label: 'Egyéb oldal',      bar: 'bg-purple-500',  text: 'text-purple-400' },
];

const SourceBreakdown = ({ totals }) => {
  const sum = SOURCE_META.reduce((acc, s) => acc + (totals?.[s.k] || 0), 0);
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="analytics-source-breakdown">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 mb-3">
        <Globe className="text-sky-400" size={14} /> Honnan jöttek az olvasók?
      </div>
      {sum === 0 ? (
        <p className="text-sm text-gray-500">Ebben az időszakban még nincs mért forgalom.</p>
      ) : (
        <>
          <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-gray-800">
            {SOURCE_META.map((s) => {
              const n = totals?.[s.k] || 0;
              if (!n) return null;
              return (
                <div
                  key={s.k}
                  className={`${s.bar} transition-all`}
                  style={{ width: `${(n / sum) * 100}%` }}
                  title={`${s.label}: ${n}`}
                />
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {SOURCE_META.map((s) => {
              const n = totals?.[s.k] || 0;
              return (
                <div key={s.k} className="flex items-baseline gap-2" data-testid={`analytics-source-${s.k}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${s.bar} shrink-0`} />
                  <span className="text-xs text-gray-400 flex-1 truncate">{s.label}</span>
                  <span className={`text-sm font-bold ${s.text}`}>{n}</span>
                  <span className="text-[10px] text-gray-600 font-mono w-10 text-right">
                    {sum ? `${Math.round((n / sum) * 100)}%` : '0%'}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default function AdminBlogAnalyticsPage() {
  const nav = useNavigate();
  const adminKey = localStorage.getItem(ADMIN_KEY_STORAGE) || '';
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('views');

  useEffect(() => {
    if (!adminKey) { nav('/admin'); return; }
    setLoading(true);
    adminBlogAnalytics(adminKey, days)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || 'Betöltés sikertelen.'))
      .finally(() => setLoading(false));
  }, [adminKey, nav, days]);

  const sorted = useMemo(() => {
    if (!data?.posts) return [];
    const arr = [...data.posts];
    const cmpNum = (a, b) => (b[sortBy] ?? 0) - (a[sortBy] ?? 0);
    return arr.sort(cmpNum);
  }, [data, sortBy]);

  if (!adminKey) return null;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-gray-400 hover:text-white"><ChevronLeft size={20} /></Link>
            <TrendingUp className="text-yellow-500" size={20} />
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Cikk figyelő — látogatottság</h1>
          </div>
          <div className="flex gap-1.5">
            {WINDOWS.map((w) => (
              <button
                key={w.d}
                data-testid={`analytics-window-${w.d}`}
                onClick={() => setDays(w.d)}
                className={`text-xs font-semibold px-3 py-1.5 rounded ${days === w.d ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 space-y-6">
        {loading && (
          <div className="flex items-center gap-2 text-gray-500 py-8">
            <Loader2 className="animate-spin" size={16} /> Betöltés…
          </div>
        )}

        {!loading && data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard icon={Eye} label="Összes megnyitás" value={data.total_views} color="text-blue-400" />
              <StatCard icon={Zap} label="Elmerülő olvasás (≥15 mp)" value={data.total_engaged} color="text-emerald-400" />
              <StatCard icon={TrendingUp} label="Elmerülési arány" value={`${data.avg_engagement}%`} color="text-yellow-400" />
              <StatCard icon={Share2} label="Összes megosztás" value={data.total_shares ?? 0} color="text-pink-400" />
              <StatCard icon={BookOpen} label="Publikált cikkek" value={data.posts.length} color="text-purple-400" />
            </div>

            <SourceBreakdown totals={data.totals_by_source} />

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-800 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-sm font-bold uppercase tracking-widest text-gray-300">Cikkek — rendezés:</h2>
                <div className="flex gap-1.5">
                  {[
                    ['views', 'Megnyitások'],
                    ['unique_visitors', 'Egyedi látogatók'],
                    ['avg_seconds', 'Átlag idő'],
                    ['engagement_pct', 'Elmerülési arány'],
                    ['shares', 'Megosztások'],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      data-testid={`analytics-sort-${k}`}
                      onClick={() => setSortBy(k)}
                      className={`text-[11px] font-semibold px-2.5 py-1.5 rounded ${sortBy === k ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="analytics-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-gray-800">
                      <th className="px-4 py-3 text-left">Cikk</th>
                      <th className="px-3 py-3 text-right">Megnyitás</th>
                      <th className="px-3 py-3 text-right">Egyedi</th>
                      <th className="px-3 py-3 text-right hidden lg:table-cell">Google</th>
                      <th className="px-3 py-3 text-right hidden lg:table-cell">Belső link</th>
                      <th className="px-3 py-3 text-right">Átlag idő</th>
                      <th className="px-3 py-3 text-right hidden md:table-cell">Össz idő</th>
                      <th className="px-3 py-3 text-right hidden md:table-cell">Elmerülés</th>
                      <th className="px-3 py-3 text-right">Megosztás</th>
                      <th className="px-3 py-3 text-right hidden md:table-cell">Utolsó</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r) => (
                      <tr key={r.slug} className="border-b border-gray-800/60 hover:bg-gray-800/40 transition" data-testid={`analytics-row-${r.slug}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <a href={`/tudastar/${r.slug}`} target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-yellow-400 line-clamp-2">
                                {r.title}
                              </a>
                              <div className="text-[10px] text-gray-500 mt-0.5 truncate flex items-center gap-1.5">
                                {r.category && <span className="px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-400 font-semibold">{r.category}</span>}
                                <span className="font-mono truncate">/tudastar/{r.slug}</span>
                                <ExternalLink size={10} className="text-gray-600 shrink-0" />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-blue-400">{r.views}</td>
                        <td className="px-3 py-3 text-right text-gray-200">{r.unique_visitors}</td>
                        <td
                          className="px-3 py-3 text-right hidden lg:table-cell font-bold text-yellow-400"
                          data-testid={`analytics-google-${r.slug}`}
                        >
                          {r.sources?.google || 0}
                        </td>
                        <td
                          className="px-3 py-3 text-right hidden lg:table-cell font-bold text-blue-300"
                          data-testid={`analytics-internal-${r.slug}`}
                        >
                          {r.sources?.internal || 0}
                        </td>
                        <td className="px-3 py-3 text-right text-emerald-300 font-mono">{r.views ? fmtSec(r.avg_seconds) : '—'}</td>
                        <td className="px-3 py-3 text-right text-gray-300 hidden md:table-cell font-mono">{r.views ? fmtSec(r.total_seconds) : '—'}</td>
                        <td className="px-3 py-3 text-right hidden md:table-cell">
                          {r.views ? (
                            <span className={`text-xs font-bold ${r.engagement_pct >= 60 ? 'text-emerald-400' : r.engagement_pct >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {r.engagement_pct}%
                            </span>
                          ) : <span className="text-gray-500">—</span>}
                        </td>
                        <td className="px-3 py-3 text-right" data-testid={`analytics-shares-${r.slug}`}>
                          {r.shares > 0 ? (
                            <div className="inline-flex items-center gap-1.5 text-pink-400 font-bold" title={Object.entries(r.share_channels || {}).map(([c, n]) => `${c}: ${n}`).join(' · ') || undefined}>
                              <Share2 size={12} />
                              <span>{r.shares}</span>
                              {r.unique_sharers > 0 && r.unique_sharers !== r.shares && (
                                <span className="text-[10px] text-pink-500/70 font-mono">({r.unique_sharers})</span>
                              )}
                            </div>
                          ) : <span className="text-gray-600">0</span>}
                        </td>
                        <td className="px-3 py-3 text-right text-gray-500 hidden md:table-cell text-xs">{r.last_view ? fmtDate(r.last_view) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 text-[11px] text-gray-500 border-t border-gray-800 bg-gray-900/60">
                💡 <strong>Elmerülő olvasás</strong>: legalább 15 másodpercet töltött az olvasó az oldalon. <strong>Google</strong> / <strong>Belső link</strong>: honnan érkezett a megnyitás (keresőből vagy az oldal egy másik aloldaláról). <strong>Megosztás</strong>: hányszor kattintottak a „Cikk megosztása" gombra (a zárójeles érték az egyedi megosztó látogatók). A tracker anonim, session-alapú (localStorage), és az admin felületről megnyitott cikkeket nem számolja. GDPR-barát — nincs PII tárolva.
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500">
      <Icon className={color} size={14} /> {label}
    </div>
    <div className={`text-2xl font-bold mt-2 ${color}`}>{value}</div>
  </div>
);
